import React, { useState } from "react";
import { useParams } from "react-router-dom";
import * as Highcharts from "highcharts";
import Boost from "highcharts/modules/boost";
import Exporting from "highcharts/modules/exporting";

import { useLiveData } from "hooks";
import { Page } from "modules/layout/Page";
import { FirebaseShard } from "firebaseWrapper";
import { getHighchartOptions } from "components/highcharts/HighchartOptions";
import { groupByField } from "extensions/groupBy";
import { deriveTitleAndUnitsFromColumnNames } from "components/athena/CsvColumnNameParsing";
import { deriveAxisFromColumnDefinitions } from "components/highcharts/HighchartOptionsFromCsv";
import { toLocalTimeFromUTCWithOffset } from "datetime";
import { HighchartsReactWithCallbackFix } from "components/highcharts/HighchartsReactWithCallbackFix";
import { Heading2 } from "components/Heading2";
import { Button } from "components/Button";
import firebase from "firebaseFactory";
import { AthenaResultsSource } from "components/athena/FetchAthenaResults";

import { DeviceStatus } from "../components/DeviceStatus";
Boost(Highcharts);
Exporting(Highcharts);

interface WaveformData {
  timestamp_utc: string;
  time_index_msec: number[];
}
type WaveformDataWithFields = WaveformData & { [key: string]: number[] };

const mapFields = (
  timestamp_utc: string,
  data: WaveformDataWithFields[],
  key: string
) =>
  data
    .flatMap((x) =>
      (x[key] || []).map((value, index) => [
        Date.parse(timestamp_utc) + x.time_index_msec[index],
        value,
      ])
    )
    .sort((x, y) => x[0] - y[0]); // ascending order

export const WaveformsPage = ({
  getWaveformData = firebase.function("getWaveformData"),
}) => {
  const { siteId, unitId, deviceId } = useParams();
  const [latestData, setLatestData] = useState<WaveformDataWithFields[]>([]);
  const { isLoading } = useLiveData(
    {
      isPaused: false,
      maxDataPoints: 14 * 4, // 4 Waveforms
      firebaseShard: FirebaseShard.Tracing,
      firebaseRef: `devices/dataBySite/${siteId}/historyByDevice/${unitId}/${deviceId}/data/waveform_data`,
      orderByDescending: true,
      onInitialData: setLatestData,
      onIncrementalData: setLatestData,
    },
    [deviceId]
  );

  // Locally calculated fields
  latestData.forEach((fields) => {
    if (fields["grid_current_a"] && fields["filter_current_a"]) {
      fields["capacitor_current_a"] = fields["grid_current_a"].map(
        (val, index) => val - fields["filter_current_a"][index]
      );
    }
  });

  const groupedData = groupByField(latestData, "timestamp_utc");

  const columnsToHideByDefault = [
    "filtered_voltage_pll_v",
    "grid_frequency_hz",
    "phase_pll_rads",
    "raw_voltage_pll_v",
    "filter_voltage_v",
    "battery_voltage_v",
    "battery_current_a",
    "filter_current_a",
    "filtered_voltage_pll_v",
    "grid_frequency_hz",
    "phase_pll_rads",
    "capacitor_current_a",
  ];
  const { columnDefinitions } = deriveTitleAndUnitsFromColumnNames([
    "raw_voltage_pll_v",
    "grid_voltage_v",
    "control_signal",
    "grid_current_a",
    "reference_current_a",
    "filter_voltage_v",
    "battery_voltage_v",
    "battery_current_a",
    "filter_current_a",
    "filtered_voltage_pll_v",
    "grid_frequency_hz",
    "phase_pll_rads",
    "capacitor_current_a",
  ]);
  const {
    yAxis,
    columnDefinitionsWithAxisIndex,
  } = deriveAxisFromColumnDefinitions(columnDefinitions);

  const [isCsvLoading, setIsCsvLoading] = useState<
    Partial<Record<string, boolean>>
  >({});
  const downloadCsv = async (timestamp_utc: string) => {
    setIsCsvLoading((x) => ({ ...x, [timestamp_utc]: true }));
    const {
      data,
    }: { data: AthenaResultsSource } = await getWaveformData.action({
      siteId,
      deviceId,
      unitId,
      waveformTimestamp: timestamp_utc,
    });
    setIsCsvLoading((x) => ({ ...x, [timestamp_utc]: false }));
    window.location.assign(data.url);
  };

  const highchartOptions: {
    timestamp_utc: string;
    options: Highcharts.Options;
  }[] =
    Object.entries(groupedData).map(([timestamp_utc, data]) => ({
      timestamp_utc,
      options: getHighchartOptions({
        yAxis: yAxis,
        dataBySeries: Object.assign(
          {},
          ...columnDefinitionsWithAxisIndex.map(
            ({ title, columnName, yAxisIndex }) => ({
              [title]: {
                dataPoints: mapFields(timestamp_utc, data, columnName),
                yAxisIndex: yAxisIndex,
                hideByDefault: columnsToHideByDefault.includes(columnName),
              },
            })
          )
        ),
      }),
    })) || [];
  // because we construct waveform data from multiple messages
  // we arbitrarily truncate the last graph (based on max data points)
  // so rather than show partial data, just drop the last graph
  const highchartOptionsWithoutLastPartialWaveform = highchartOptions.slice(
    0,
    highchartOptions.length - 1
  );
  return (
    <Page
      isLoading={isLoading}
      titleSupplement={
        <DeviceStatus
          siteId={siteId}
          unitId={unitId}
          deviceId={deviceId}
          className="ml-2"
        />
      }
    >
      <div className="flex flex-wrap -mx-2">
        {highchartOptionsWithoutLastPartialWaveform.map(
          ({ timestamp_utc, options }, index) => (
            <div className="w-full px-2" key={index}>
              <div className="flex justify-between mb-2">
                <Heading2>
                  {toLocalTimeFromUTCWithOffset(timestamp_utc)}
                </Heading2>
                <Button
                  isLoading={isCsvLoading[timestamp_utc]}
                  onClick={() => downloadCsv(timestamp_utc)}
                >
                  {isCsvLoading[timestamp_utc]
                    ? "Please wait..."
                    : "Download CSV"}
                </Button>
              </div>
              <div className="rounded-lg shadow-lg overflow-hidden mb-6">
                <HighchartsReactWithCallbackFix
                  highcharts={Highcharts}
                  options={options}
                />
              </div>
            </div>
          )
        )}
      </div>
    </Page>
  );
};
